// Shape a JobResultsResponseDTO into the view model the page renders. Ported from the prototype's
// normalize(), but aligned to the spec where they differ: errors[] are table/file-level (not per-field),
// "failed" is row-based (read − masked), and `fields` are { field, algorithm } only (no per-field counts).

export function fmtNum(n) {
  return n == null ? '—' : Number(n).toLocaleString();
}

export function fmtDuration(ms) {
  if (ms == null || isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

/** Group a unit's fields by algorithm, counting and flagging which algorithms appear in errors[]. */
function buildAlgGroups(fields, errors) {
  const upperErrors = (errors || []).map((e) => String(e).toUpperCase());
  const map = new Map();
  (fields || []).forEach((f) => {
    const alg = f.algorithm || 'UNKNOWN';
    const group = map.get(alg) || { alg, count: 0, failed: false };
    group.count += 1;
    if (!group.failed && upperErrors.some((e) => e.includes(alg.toUpperCase()))) {
      group.failed = true;
    }
    map.set(alg, group);
  });
  return [...map.values()].sort((a, b) => b.count - a.count);
}

export function normalize(job) {
  const p = job && job.payload;
  if (!p) return { purged: true, job };

  const isFile = p.jobType === 'FILE';
  const isNoSql = p.jobType === 'NOSQL';
  const rawUnits = (isFile ? p.files : isNoSql ? p.collections : p.tables) || [];

  const units = rawUnits.map((u) => {
    // NoSQL: DescribeTable/countDocuments can be unavailable, and 0 is indistinguishable
    // from "unknown" — render as — rather than implying an empty collection.
    const total = isFile ? u.totalRecords : isNoSql ? (u.totalDocuments > 0 ? u.totalDocuments : null) : u.totalRows;
    const read = (isFile ? u.recordsRead : isNoSql ? u.documentsRead : u.rowsRead) || 0;
    const masked = (isFile ? u.recordsWritten : isNoSql ? u.documentsWritten : u.rowsUpdated) || 0;
    const skipped = (isFile || isNoSql ? 0 : u.resumedRowsSkipped) || 0;
    const fields = u.fields || [];
    const hardErrors = u.errors || [];
    // NoSQL warnings (e.g. a dropped fan-out target) are shown in the errors block but do
    // NOT mark the collection failed — the masking pass itself succeeded.
    const warnings = isNoSql ? (u.warnings || []) : [];
    const errors = warnings.length > 0
      ? [...hardErrors, ...warnings.map((w) => `Warning: ${w}`)]
      : hardErrors;
    const failedRows = Math.max(0, read - masked);
    let level = 'clean';
    if (failedRows > 0 || hardErrors.length > 0) level = 'failed';
    else if (skipped > 0 || warnings.length > 0) level = 'skipped';
    return {
      name: isFile
        ? u.fileName
        : isNoSql
          ? (u.database ? `${u.database}.${u.collectionName}` : u.collectionName)
          : u.tableName,
      total, read, masked, skipped, fields, errors, failedRows, level,
      algGroups: buildAlgGroups(fields, hardErrors),
    };
  });

  const totals = units.reduce(
    (acc, u) => ({
      masked: acc.masked + (u.masked || 0),
      fields: acc.fields + u.fields.length,
      failedRows: acc.failedRows + u.failedRows,
      errors: acc.errors + u.errors.length,
    }),
    { masked: 0, fields: 0, failedRows: 0, errors: 0 }
  );

  const queueMs = new Date(p.startedAt) - new Date(p.submittedAt);
  const runMs = new Date(p.completedAt) - new Date(p.startedAt);
  const totalMs = new Date(p.completedAt) - new Date(p.submittedAt);

  return { purged: false, isFile, isNoSql, payload: p, units, totals, queueMs, runMs, totalMs, job };
}
